/**
 * Per-guest delivery status for group broadcasts, as pure functions.
 *
 * Two different questions live on one recipient row and must never be blended:
 *
 *   - `status` ('sent' | 'failed' | 'skipped'): did the messaging provider
 *     ACCEPT the send. Synchronous, written once. Existing counters read it.
 *   - `deliveryStatus` (sent | delivered | seen | clicked | failed | null): what
 *     the provider LATER reported, out of band, via its webhook.
 *
 * The rule this file exists to hold: a null `deliveryStatus` means "no report",
 * never "didn't arrive". Reports only exist once the owner has configured the
 * provider's webhook, so on a fresh environment every row is null, and painting
 * that as failure would send a host chasing forty guests who did hear from them.
 * Absent fields (an older API) are read exactly like null.
 */
import { CHANNEL_ORDER, isChannel, shortChannelLabel, type BroadcastChannel } from './broadcastChannels';
import type { GroupBroadcastRecipient, GroupBroadcastSummary } from '../types/groups';

export type DeliveryState =
  /** Skipped, or the provider refused it at send time. Nothing left to wait for. */
  | 'not_sent'
  /** Provider accepted it; no delivery report (yet, or ever). */
  | 'accepted_no_report'
  /** The provider's first, weakest report: handed to the carrier / mail server. */
  | 'sent_by_provider'
  | 'delivered'
  | 'seen'
  | 'clicked'
  /** The provider accepted it, then reported it did not arrive. */
  | 'delivery_failed';

/** How long after a send we keep asking for late reports. */
export const FRESH_BROADCAST_WINDOW_MS = 30 * 60 * 1000;
export const DELIVERY_POLL_INTERVAL_MS = 30_000;

export const DELIVERY_REPORTS_OFF_NOTICE =
  "Delivery reports aren't switched on yet — 'sent' means handed to the messaging provider, not received.";

/** Shown wherever a fresh send is confirmed, where arrival cannot be known yet. */
export const HANDED_OFF_NOTE =
  "'Sent' means handed to the messaging provider. Whether it arrived shows in Sent history.";

function clean(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * The state of one recipient row.
 *
 * Only a row the provider accepted can have a delivery state. Anything the
 * provider reported that we do not recognise reads as "no report": an unknown
 * word is never allowed to become a failure.
 */
export function deliveryState(
  recipient: Pick<GroupBroadcastRecipient, 'status' | 'deliveryStatus'>,
): DeliveryState {
  if (clean(recipient.status) !== 'sent') return 'not_sent';
  switch (clean(recipient.deliveryStatus)) {
    case 'sent':
      return 'sent_by_provider';
    case 'delivered':
      return 'delivered';
    case 'seen':
      return 'seen';
    case 'clicked':
      return 'clicked';
    case 'failed':
      return 'delivery_failed';
    default:
      // null, absent, empty, or a status this Studio has never heard of.
      return 'accepted_no_report';
  }
}

export interface DeliveryCounts {
  /** Rows the provider accepted (status 'sent'). One row per guest per channel. */
  accepted: number;
  /** delivered + seen + clicked: a report that it reached the guest. */
  delivered: number;
  /** delivered/seen/clicked that were actually opened or clicked (subset of delivered). */
  engaged: number;
  /** Accepted, then the provider reported it did not arrive. */
  deliveryFailed: number;
  /** The provider refused it at send time. */
  failedAtSend: number;
  /** deliveryFailed + failedAtSend. */
  failed: number;
  /** Accepted, and no delivery report at all. */
  noReport: number;
  /** Accepted, and the provider's only report is the weak "sent". Still awaiting a verdict. */
  providerSent: number;
  skipped: number;
}

export interface DeliverySummary {
  overall: DeliveryCounts;
  /** Only channels that actually have rows. */
  byChannel: Partial<Record<BroadcastChannel, DeliveryCounts>>;
}

function emptyCounts(): DeliveryCounts {
  return {
    accepted: 0,
    delivered: 0,
    engaged: 0,
    deliveryFailed: 0,
    failedAtSend: 0,
    failed: 0,
    noReport: 0,
    providerSent: 0,
    skipped: 0,
  };
}

function tally(counts: DeliveryCounts, recipient: GroupBroadcastRecipient): void {
  const state = deliveryState(recipient);
  const status = clean(recipient.status);
  if (state === 'not_sent') {
    if (status === 'failed') {
      counts.failedAtSend += 1;
      counts.failed += 1;
    } else if (status === 'skipped') {
      counts.skipped += 1;
    }
    return;
  }
  counts.accepted += 1;
  if (state === 'delivered' || state === 'seen' || state === 'clicked') {
    counts.delivered += 1;
    if (state !== 'delivered') counts.engaged += 1;
  } else if (state === 'delivery_failed') {
    counts.deliveryFailed += 1;
    counts.failed += 1;
  } else if (state === 'sent_by_provider') {
    counts.providerSent += 1;
  } else {
    counts.noReport += 1;
  }
}

/**
 * Rows written before the channel column existed carry none, and were
 * email-only by definition (the same reading broadcastHistoryCounts uses).
 */
function channelOf(recipient: GroupBroadcastRecipient): BroadcastChannel {
  const channel = clean(recipient.channel);
  return isChannel(channel) ? channel : 'email';
}

export function summarizeDelivery(
  recipients: readonly GroupBroadcastRecipient[] | null | undefined,
): DeliverySummary {
  const overall = emptyCounts();
  const byChannel: Partial<Record<BroadcastChannel, DeliveryCounts>> = {};
  for (const recipient of recipients ?? []) {
    tally(overall, recipient);
    const channel = channelOf(recipient);
    const counts = byChannel[channel] ?? (byChannel[channel] = emptyCounts());
    tally(counts, recipient);
  }
  return { overall, byChannel };
}

/**
 * True when ANY recipient in the given history has a non-null delivery status.
 * That is the only evidence we have that the provider's webhook is switched on:
 * it tells "reports aren't configured" apart from "reports are pending".
 */
export function deliveryReportsExpected(
  recipients: readonly Pick<GroupBroadcastRecipient, 'deliveryStatus'>[] | null | undefined,
): boolean {
  return (recipients ?? []).some((recipient) => clean(recipient.deliveryStatus).length > 0);
}

/** Every recipient across a loaded history, flattened. */
export function historyRecipients(
  history: readonly Pick<GroupBroadcastSummary, 'recipients'>[] | null | undefined,
): GroupBroadcastRecipient[] {
  return (history ?? []).flatMap((broadcast) => broadcast.recipients ?? []);
}

/** The one info line shown above the history when nothing has ever been reported. */
export function deliveryReportsOffNotice(
  history: readonly Pick<GroupBroadcastSummary, 'recipients'>[] | null | undefined,
): string | null {
  const recipients = historyRecipients(history);
  if (deliveryReportsExpected(recipients)) return null;
  // Nothing was accepted, so there is nothing that could have been reported on.
  if (!recipients.some((recipient) => deliveryState(recipient) !== 'not_sent')) return null;
  return DELIVERY_REPORTS_OFF_NOTICE;
}

export interface DeliveryChip {
  label: string;
  color: 'default' | 'success' | 'error' | 'info';
  /** Hover text; the failure reason for a failed delivery. */
  tooltip?: string;
}

/**
 * The per-recipient delivery chip, or null for "say nothing extra".
 *
 * `reportsExpected` is deliveryReportsExpected() over the loaded history. When it
 * is false, every accepted row is null by construction, so a "No report yet" chip
 * on each would be noise that reads like a fault.
 */
export function deliveryChip(
  recipient: Pick<GroupBroadcastRecipient, 'status' | 'deliveryStatus' | 'deliveryDetail'>,
  reportsExpected: boolean,
): DeliveryChip | null {
  switch (deliveryState(recipient)) {
    case 'not_sent':
      return null;
    case 'accepted_no_report':
      return reportsExpected
        ? { label: 'No report yet', color: 'default', tooltip: 'The messaging provider has not reported on this one.' }
        : null;
    case 'sent_by_provider':
      return { label: 'Provider accepted', color: 'default', tooltip: 'Handed to the carrier or mail server; no arrival report yet.' };
    case 'delivered':
      return { label: 'Delivered', color: 'success' };
    case 'seen':
      return { label: 'Opened', color: 'success' };
    case 'clicked':
      return { label: 'Clicked', color: 'success' };
    case 'delivery_failed': {
      const detail = String(recipient.deliveryDetail ?? '').trim();
      return {
        label: "Didn't arrive",
        color: 'error',
        tooltip: detail || 'The messaging provider reported this did not arrive.',
      };
    }
  }
}

function channelName(channel: BroadcastChannel): string {
  return channel === 'sms' ? 'Text' : 'Email';
}

function countParts(counts: DeliveryCounts): string[] {
  const parts = [`${counts.accepted} sent`];
  if (counts.delivered > 0) parts.push(`${counts.delivered} delivered`);
  if (counts.failed > 0) parts.push(`${counts.failed} failed`);
  const waiting = counts.noReport + counts.providerSent;
  if (waiting > 0) parts.push(`${waiting} no report yet`);
  return parts;
}

/**
 * "12 sent · 9 delivered · 1 failed · 2 no report yet"; split by channel only
 * when both were used: "Email: 10 sent, 10 delivered · Text: 2 sent, 1 failed".
 *
 * Null when there is nothing beyond what the card's own "Sent 12 email" line
 * already says, i.e. no provider reports anywhere in the history. Counts are
 * rows (one per guest per channel), which is what "messages" means here.
 * "No report yet" rather than "awaiting": for an old broadcast on a group whose
 * webhook was only switched on later, no report is coming, and "awaiting" would
 * promise one.
 */
export function deliverySummaryLine(
  recipients: readonly GroupBroadcastRecipient[] | null | undefined,
  reportsExpected: boolean,
): string | null {
  if (!reportsExpected) return null;
  const { overall, byChannel } = summarizeDelivery(recipients);
  if (overall.accepted + overall.failed === 0) return null;
  const channels = CHANNEL_ORDER.filter((channel) => byChannel[channel]);
  if (channels.length <= 1) return countParts(overall).join(' · ');
  return channels
    .map((channel) => `${channelName(channel)}: ${countParts(byChannel[channel]!).join(', ')}`)
    .join(' · ');
}

/**
 * "1 text didn't arrive" / "2 emails didn't arrive", one per channel, for the
 * collapsed row. Only reported delivery failures: a refused send is already on
 * the card as "N failed" (broadcastHistoryCounts) and is not a delivery failure.
 */
export function deliveryFailureBadges(
  recipients: readonly GroupBroadcastRecipient[] | null | undefined,
): string[] {
  const { byChannel } = summarizeDelivery(recipients);
  const badges: string[] = [];
  for (const channel of CHANNEL_ORDER) {
    const n = byChannel[channel]?.deliveryFailed ?? 0;
    if (n === 0) continue;
    const noun = channel === 'sms' ? 'text' : 'email';
    badges.push(`${n} ${noun}${n === 1 ? '' : 's'} didn't arrive`);
  }
  return badges;
}

/** Short channel label for a row, re-exported so the card has one import. */
export function rowChannelLabel(recipient: Pick<GroupBroadcastRecipient, 'channel'>): string {
  return shortChannelLabel(clean(recipient.channel) || 'email');
}

/**
 * Should the history refetch itself?
 *
 * Only when all three hold: the provider has reported on something before (so
 * the webhook is on and a report is genuinely coming), a broadcast is fresh
 * (reports land within minutes; an old silent row is not going to change), and
 * some row of a fresh one is still waiting for its verdict. `sent_by_provider`
 * waits too: it is the provider's first word, and "delivered" follows it.
 */
export function shouldPollDeliveryReports(
  history: readonly Pick<GroupBroadcastSummary, 'createdAt' | 'recipients'>[] | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!history || history.length === 0) return false;
  if (!deliveryReportsExpected(historyRecipients(history))) return false;
  return history.some((broadcast) => {
    const created = Date.parse(broadcast.createdAt);
    if (Number.isNaN(created) || now - created > FRESH_BROADCAST_WINDOW_MS) return false;
    return (broadcast.recipients ?? []).some((recipient) => {
      const state = deliveryState(recipient);
      return state === 'accepted_no_report' || state === 'sent_by_provider';
    });
  });
}

/** react-query `refetchInterval` callback. False means do not poll. */
export function deliveryRefetchInterval(
  history: readonly Pick<GroupBroadcastSummary, 'createdAt' | 'recipients'>[] | null | undefined,
): number | false {
  return shouldPollDeliveryReports(history) ? DELIVERY_POLL_INTERVAL_MS : false;
}
